import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { gsap } from "gsap/all";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import fragmentShader from "./shaders/fragment.glsl";
import vertexShader from "./shaders/vertex.glsl";
import { events, math, store } from "../../src/js/core/store.js";

export class Canvas {
  constructor() {
    this.placeholder = document.querySelector(".js-insert-canvas");
    this.setBounds();
    this.rotated = false;
    this.inView = false;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(75, this.viewWidth / this.viewHeight, 0.1, 100);

    if (window.innerWidth < 800) {
      this.camera.position.set(-2, 0.07, 0.8);
    } else {
      this.camera.position.set(-1.7, 0.07, 0.62);
    }

    this.raycaster = new THREE.Raycaster();
    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enabled = false;
    this.controls.enablePan = false;
    this.controls.enableZoom = false;
    this.controls.enableDamping = true;
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = false;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.viewWidth, this.viewHeight);
    this.renderer.outputEncoding = THREE.sRGBEncoding;

    this.observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          this.play();
        } else {
          this.pause();
        }
      });
    });

    this.observer.observe(this.placeholder);
    this.init();
  }

  initGui() {
    this.gui = new dat.GUI();

    this.settings = {
      x: 0,
      y: 0,
      z: 0,
    };

    this.gui
      .add(this.settings, "x", -10, 10)
      .step(0.01)
      .onChange((value) => {
        this.camera.position.x = value;
      });

    this.gui
      .add(this.settings, "y", -5, 5)
      .step(0.01)
      .onChange((value) => {
        this.camera.position.y = value;
      });

    this.gui
      .add(this.settings, "z", -5, 5)
      .step(0.01)
      .onChange((value) => {
        this.camera.position.z = value;
      });
  }

  setBounds() {
    const bounds = this.placeholder.getBoundingClientRect();
    this.viewWidth = bounds.width;
    this.viewHeight = bounds.height;
  }

  loadAfterTexture() {
    new THREE.TextureLoader().load(
      "/wp-content/themes/justdiggit2021/globe/DiffuseMap-002.jpg",
      (texture) => {
        texture.flipY = false;
        texture.encoding = THREE.sRGBEncoding;
        this.mesh.material.uniforms.uTexture2.value = texture;
      }
    );
  }

  loadModel() {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("/wp-content/themes/justdiggit2021/globe/draco/");

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load("/wp-content/themes/justdiggit2021/globe/model-draco.gltf", (gltf) => {
      this.object = gltf.scene;
      this.object.rotation.y = THREE.Math.degToRad(-90);
      this.scene.add(this.object);

      this.object.traverse((model) => {
        if (model.isMesh) {
          this.mesh = model;

          this.mesh.material = new THREE.ShaderMaterial({
            precision: "lowp",
            uniforms: {
              uProgress: { value: 0 },
              uTexture1: { value: this.mesh.material.map },
              uTexture2: { value: null },
            },
            vertexShader,
            fragmentShader,
          });

          this.mesh.castShadow = false;
          this.mesh.receiveShadow = false;
          this.loadAfterTexture();
          this.render();
        }
      });
    });
  }

  addLighting() {
    this.ambientLight = new THREE.AmbientLight(0xffffff, 1);
    this.scene.add(this.ambientLight);
  }

  bindResize() {
    window.addEventListener("resize", this.resize);
  }

  addToDom() {
    const canvas = this.renderer.domElement;
    this.placeholder.appendChild(canvas);
  }

  rotate() {
    gsap.to(this.mesh.material.uniforms.uProgress, 2, {
      value: 1,
      delay: 3,
      ease: "power4.inOut",
    });

    gsap.to(this.object.rotation, 5, {
      y: this.object.rotation.y + THREE.Math.degToRad(360 * 5),
      ease: "power4.inOut",
      onComplete: () => {
        this.rotated = true;
        this.controls.enabled = true;
      },
    });
  }

  updatePoints() {
    for (const point of this.points) {
      const screenPosition = point.position.clone();
      screenPosition.project(this.camera);

      this.raycaster.setFromCamera(screenPosition, this.camera);
      const intersects = this.raycaster.intersectObjects(this.scene.children, true);

      if (!this.rotated) {
        point.element.classList.remove("visible");
      }

      if (intersects.length === 0) {
        if (this.rotated) {
          point.element.classList.add("visible");
        }
      } else {
        const intersectionDistance = intersects[0].distance + 0.005;
        const pointDistance = point.position.distanceTo(this.camera.position);
        if (point.name === "") {
          console.log(intersectionDistance, pointDistance);
        }

        if (intersectionDistance < pointDistance) {
          point.element.classList.remove("visible");
        } else {
          if (this.rotated) {
            point.element.classList.add("visible");
          }
        }
      }

      const translateX = screenPosition.x * this.viewWidth * 0.5;
      const translateY = -screenPosition.y * this.viewHeight * 0.5;
      point.element.style.transform = `translate3d(calc(${translateX}px - 50%), calc(${translateY}px - 50%), 0)`;
    }
  }

  addPoints() {
    this.points = [];

    Array.from(document.querySelectorAll(".js-point")).forEach((point) => {
      this.points.push({
        position: calcPosFromLatLonRad(
          point.getAttribute("data-lat"),
          point.getAttribute("data-lng"),
          1.033
        ),
        element: point,
        name: point.dataset.title,
      });
    });
  }

  resize = () => {
    this.setBounds();
    this.camera.aspect = this.viewWidth / this.viewHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.viewWidth, this.viewHeight);
    this.render();
  };

  pause = () => {
    this.inView = false;
  };

  play = () => {
    this.inView = true;
  };

  render = () => {
    this.controls.update();
    this.updatePoints();
    this.renderer.render(this.scene, this.camera);
  };

  init() {
    this.addToDom();
    this.addPoints();
    this.bindResize();
    this.loadModel();
    this.addLighting();
  }
}

function calcPosFromLatLonRad(lat, lon, radius) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);

  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const y = radius * Math.cos(phi);
  const z = radius * Math.sin(phi) * Math.sin(theta);
  // const z = 1

  return new THREE.Vector3(x, y, z);
}
